import { Account } from '@brenger/api-client';
import { useModalState } from '@brenger/react';
import { subMonths } from 'date-fns';
import React from 'react';
import { Translate } from 'react-localize-redux-dep-updated';
import { Button, Dialog, IconInfo, Modal } from '../../../brenger-shared-ui';
import { useTypedSelector } from '../../../hooks';
import { getActiveLanguageSettings, translate } from '../../../utils/localization';
import { basisPointsAsString } from '../../../utils/price';
import { getDiscountPercentageBasisPoints } from '../ducks';

export const DiscountBanner = (): JSX.Element | null => {
  const modal = useModalState();
  const accountName = useTypedSelector(state => (state.user.userData?.account as Account | undefined)?.name);
  const discountPercentageBasisPoints = useTypedSelector(getDiscountPercentageBasisPoints);

  if (!discountPercentageBasisPoints) return null;

  const percentage = basisPointsAsString(discountPercentageBasisPoints);
  const transVars = {
    previous_month: getMonthName('previous'),
    current_month: getMonthName('current'),
    percentage: <span className={'text-green-500'}>{percentage}%</span>,
    company_name: accountName,
  };

  return (
    <>
      <div
        className={
          'flex items-start justify-between gap-3 py-3 px-4 mb-4 mr-6 md:mr-0 bg-white border border-blue-400 rounded'
        }
        data-qa-id="discount-banner"
      >
        <div className={'min-w-0'}>
          <div className={'text-xs uppercase text-black'} style={{ fontWeight: 'bold' }}>
            <Translate id={'request_flow.price.volume_discount_banner.context'} data={transVars} />
          </div>
          <div className={'text-primary text-sm'} style={{ fontWeight: 'bold' }}>
            <Translate id={'request_flow.price.volume_discount_banner.title'} data={transVars} />
          </div>
        </div>
        <button type="button" className={'btn-no-style shrink-0'} onClick={modal.open}>
          <IconInfo color={'blue'} />
        </button>
      </div>
      <Dialog isActive={modal.isOpen}>
        <Modal
          open={modal.isOpen}
          onClick={modal.close}
          header={
            <h5>
              <Translate
                id={'request_flow.price.volume_discount_banner.modal.title'}
                data={{ ...transVars, percentage: `${percentage}%` }}
              />
            </h5>
          }
        >
          <div
            className={'pb-4'}
            dangerouslySetInnerHTML={{
              // eslint-disable-next-line @typescript-eslint/naming-convention
              __html: String(translate('request_flow.price.volume_discount_banner.modal.body', transVars)),
            }}
          />
          <Button buttonStyle={'secondary'} fullWidth={true} onClick={modal.close}>
            <Translate id={'request_flow.price.volume_discount_banner.modal.button'} />
          </Button>
        </Modal>
      </Dialog>
    </>
  );
};

/**
 * Volume discount is earned on last month's transports, so the banner names that period
 * rather than the month the customer is booking in.
 */
const getMonthName = (type: 'previous' | 'current'): string => {
  const {
    labels: { short, countryCode },
  } = getActiveLanguageSettings();

  return new Intl.DateTimeFormat(`${short}-${countryCode}`, { month: 'long' }).format(
    type === 'previous' ? subMonths(new Date(), 1) : new Date()
  );
};
